import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { CalendarRange, Check, X, Clock } from "lucide-react";

const TYPE_LABEL = {
  holiday: "Ferie",
  permission: "Permesso",
  rol: "ROL",
  law_104: "Legge 104",
  parental_leave: "Congedo parentale",
  medical_visit: "Visita medica",
  union: "Sindacale",
  illness: "Malattia",
  other: "Altro",
};

const STATUS = [
  { id: "pending", label: "In attesa", icon: Clock, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  { id: "approved", label: "Approvate", icon: Check, tone: "text-green-700 bg-green-50 border-green-200" },
  { id: "rejected", label: "Rifiutate", icon: X, tone: "text-red-700 bg-red-50 border-red-200" },
];

const statusPill = (s) => {
  const cfg = { pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };
  const label = { pending: "In attesa", approved: "Approvata", rejected: "Rifiutata" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${cfg[s] || "bg-gray-100"}`}>{label[s] || s}</span>;
};

export default function LeavesPage() {
  const [filter, setFilter] = useState("pending");
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState({});
  const [decideFor, setDecideFor] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [note, setNote] = useState("");

  const load = async () => {
    const params = filter && filter !== "all" ? `?status=${filter}` : "";
    const [l, e] = await Promise.all([api.get(`/leaves${params}`), api.get("/employees")]);
    setLeaves(l.data);
    const map = {};
    e.data.forEach((x) => { map[x.id] = x; });
    setEmployees(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDecide = (l, status) => { setDecideFor(l); setDecision(status); setNote(""); };
  const submitDecision = async () => {
    try {
      await api.patch(`/leaves/${decideFor.id}/decision`, { status: decision, decision_note: note });
      toast.success(decision === "approved" ? "Richiesta approvata" : "Richiesta rifiutata");
      setDecideFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const empName = (eid) => {
    const e = employees[eid];
    return e ? `${e.name} ${e.lastname}` : eid?.slice(0, 8);
  };
  const empJob = (eid) => employees[eid]?.job || "—";

  return (
    <div className="p-4 md:p-12" data-testid="leaves-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Risorse Umane</div>
        <h1 className="font-display text-4xl">Ferie & Permessi</h1>
        <p className="text-[#555] mt-1 text-sm">Gestisci le richieste dei dipendenti · approva o rifiuta</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {[{id:"all",label:"Tutte",icon:CalendarRange,tone:""}, ...STATUS].map((s) => {
          const Icon = s.icon;
          const active = filter === s.id;
          return (
            <button key={s.id} onClick={()=>setFilter(s.id)} data-testid={`leaves-filter-${s.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-all ${active ? "bg-[#3B82F6] text-white shadow-lg shadow-[#3B82F6]/30" : "bg-white border border-[#1F2540] hover:border-[#3B82F6]"}`}>
              <Icon className="w-4 h-4"/> {s.label}
            </button>
          );
        })}
      </div>

      <div className="card-smart overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#1F2540]"><tr className="text-left">
            <th className="p-4">Dipendente</th><th className="p-4">Tipo</th><th className="p-4">Periodo</th><th className="p-4">Ore</th><th className="p-4">Note</th><th className="p-4">Stato</th><th className="p-4"></th>
          </tr></thead>
          <tbody>
            {leaves.length === 0 && <tr><td colSpan="7" className="p-12 text-center text-[#757575]"><CalendarRange className="w-10 h-10 mx-auto mb-3 text-[#3B82F6]/50"/>Nessuna richiesta con questo filtro.</td></tr>}
            {leaves.map((l) => (
              <tr key={l.id} className="border-b border-[#1F2540] hover:bg-[#1E2347]/50" data-testid={`leave-${l.id}`}>
                <td className="p-4"><div className="font-semibold">{empName(l.employee_id)}</div><div className="text-xs text-[#757575]">{empJob(l.employee_id)}</div></td>
                <td className="p-4 font-semibold">{TYPE_LABEL[l.type] || l.type}</td>
                <td className="p-4 text-xs">{l.start_date} <span className="text-[#757575]">→</span> {l.end_date}</td>
                <td className="p-4 text-xs">{l.hours ?? "—"}</td>
                <td className="p-4 text-xs text-[#757575]">
                  {l.notes}
                  {l.decision_note && <div className="text-[10px] italic mt-1">Risposta: {l.decision_note}</div>}
                </td>
                <td className="p-4">{statusPill(l.status)}</td>
                <td className="p-4 text-right">
                  {l.status === "pending" && (
                    <>
                      <Button size="icon" onClick={()=>openDecide(l, "approved")} data-testid={`approve-${l.id}`} className="bg-green-600 hover:bg-green-700 rounded-full h-8 w-8 mr-1"><Check className="w-4 h-4"/></Button>
                      <Button size="icon" onClick={()=>openDecide(l, "rejected")} data-testid={`reject-${l.id}`} className="bg-red-600 hover:bg-red-700 rounded-full h-8 w-8"><X className="w-4 h-4"/></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!decideFor} onOpenChange={(v)=>{ if(!v) setDecideFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-md" data-testid="leave-decide-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{decision === "approved" ? "Approva richiesta" : "Rifiuta richiesta"}</DialogTitle></DialogHeader>
          {decideFor && (
            <div className="space-y-4">
              <div className="text-sm bg-[#1E2347]/50 border border-[#1F2540] rounded-xl p-3">
                <div className="font-semibold">{empName(decideFor.employee_id)} · {TYPE_LABEL[decideFor.type]}</div>
                <div className="text-xs text-[#757575]">{decideFor.start_date} → {decideFor.end_date}</div>
                {decideFor.notes && <div className="text-xs mt-1">{decideFor.notes}</div>}
              </div>
              <div><Label>Nota per il dipendente (opzionale)</Label><Input data-testid="decision-note" className="rounded-xl mt-1" value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Es: Approvato, ricordati di lasciare il pc in ufficio"/></div>
              <DialogFooter>
                <Button onClick={submitDecision} data-testid="decision-submit" className={`rounded-full ${decision === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}>
                  Conferma {decision === "approved" ? "approvazione" : "rifiuto"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
